import type { CSSProperties } from 'react'
import { cx, type PidBaseProps } from './shared'
import { U } from './PidDiagram'

export type PipeKind = 'straight' | 'elbow' | 'tee' | 'cap'
export type PipeStroke = 'solid' | 'dashed' | 'dotted'

/** Elbow: the two open sides the corner connects. */
export type ElbowDirection = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
/** Straight / tee / cap direction. */
export type Cardinal = 'up' | 'down' | 'left' | 'right'

export interface PipeProps extends PidBaseProps {
  kind?: PipeKind
  /** Lattice units for straight runs. Ignored for elbow/tee/cap. */
  length?: number
  /**
   * Straight: which way the run extends from the lattice point.
   * Elbow (90° shortcuts): which two sides the corner opens to (`top-right`, …).
   * Tee: which side is the branch.
   * Cap: which side the run arrives from.
   */
  direction?: ElbowDirection | Cardinal
  /**
   * Elbow only: included angle between the two legs, in degrees.
   * Omit (with a named `direction`) for the flush lattice 90° corner paths.
   * Use with `rotation`, or set `from`/`to` for absolute leg angles.
   */
  degrees?: number
  /**
   * Elbow only: angle of the first leg in degrees.
   * `0` = right, `90` = down, `180` = left, `270` = up (clockwise, SVG y-down).
   * Second leg is at `rotation + degrees`.
   */
  rotation?: number
  /**
   * Elbow only: absolute start/end leg angles (same convention as `rotation`).
   * When both are set they win over `degrees`/`rotation`/`direction`.
   */
  from?: number
  to?: number
  /** Any CSS color. */
  color?: string
  /** Stroke pattern. */
  stroke?: PipeStroke
  /** Stroke width in px. */
  thickness?: number
  /** Draw a flow chevron mid-run (straight only). */
  flow?: boolean
  /**
   * Lattice mode (default): SVG is absolutely offset so the stroke centerline
   * passes through the parent `<At>` point. Set false for gallery / document flow.
   */
  anchored?: boolean
}

const DEFAULT_COLOR = 'var(--pipe-process, #3f3f46)'

/**
 * Pipe primitives are meant to sit in `<At col row>`.
 * Appearance is fully prop-driven — no named media presets.
 */
export function Pipe({
  length = 4,
  kind = 'straight',
  direction = 'right',
  degrees,
  rotation,
  from,
  to,
  color = DEFAULT_COLOR,
  stroke = 'solid',
  thickness = 3,
  flow = false,
  anchored = true,
  className,
  style,
  title
}: PipeProps) {
  const anchorClass = anchored ? 'pid-pipe-anchor' : undefined
  const dash = dashFor(stroke, thickness)
  const strokeProps = {
    stroke: color,
    strokeWidth: thickness,
    strokeDasharray: dash,
    strokeLinecap: (stroke === 'solid' ? 'square' : 'butt') as 'square' | 'butt',
    strokeLinejoin: 'miter' as const,
    fill: 'none' as const
  }

  if (kind === 'elbow') {
    const path = resolveElbowPath({ direction, degrees, rotation, from, to })
    return (
      <svg
        className={cx('pid-pipe', anchorClass, className)}
        width={U}
        height={U}
        viewBox={`0 0 ${U} ${U}`}
        style={anchored ? { ...style, left: -U / 2, top: -U / 2 } : style}
        aria-hidden={!title}
        role={title ? 'img' : undefined}
      >
        {title ? <title>{title}</title> : null}
        <path d={path} {...strokeProps} />
      </svg>
    )
  }

  if (kind === 'tee') {
    const branch = asCardinal(direction)
    return (
      <svg
        className={cx('pid-pipe', anchorClass, className)}
        width={U}
        height={U}
        viewBox={`0 0 ${U} ${U}`}
        style={anchored ? { ...style, left: -U / 2, top: -U / 2 } : style}
        aria-hidden={!title}
        role={title ? 'img' : undefined}
      >
        {title ? <title>{title}</title> : null}
        <path d={teePath(branch)} {...strokeProps} />
      </svg>
    )
  }

  if (kind === 'cap') {
    const from = asCardinal(direction)
    return (
      <svg
        className={cx('pid-pipe', anchorClass, className)}
        width={U}
        height={U}
        viewBox={`0 0 ${U} ${U}`}
        style={anchored ? { ...style, left: -U / 2, top: -U / 2 } : style}
        aria-hidden={!title}
        role={title ? 'img' : undefined}
      >
        {title ? <title>{title}</title> : null}
        <path d={capPath(from)} {...strokeProps} />
        <circle cx={12} cy={12} r={Math.max(2, thickness * 0.85)} fill={color} stroke="none" />
      </svg>
    )
  }

  const cardinal = asCardinal(direction)
  const horizontal = cardinal === 'left' || cardinal === 'right'
  const major = length * U
  const w = horizontal ? major : U
  const h = horizontal ? U : major

  const offsetStyle: CSSProperties | undefined = anchored
    ? horizontal
      ? { ...style, left: cardinal === 'left' ? -major : 0, top: -U / 2, width: w, height: h }
      : { ...style, left: -U / 2, top: cardinal === 'up' ? -major : 0, width: w, height: h }
    : style

  const x1 = horizontal ? 0 : U / 2
  const y1 = horizontal ? U / 2 : 0
  const x2 = horizontal ? major : U / 2
  const y2 = horizontal ? U / 2 : major

  const arrow =
    flow && horizontal
      ? arrowPointsHorizontal(major, U / 2, cardinal === 'left')
      : flow && !horizontal
        ? arrowPointsVertical(U / 2, major, cardinal === 'up')
        : null

  return (
    <svg
      className={cx('pid-pipe', anchorClass, className)}
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={offsetStyle}
      aria-hidden={!title}
      role={title ? 'img' : undefined}
    >
      {title ? <title>{title}</title> : null}
      <line x1={x1} y1={y1} x2={x2} y2={y2} {...strokeProps} />
      {arrow && <polygon points={arrow} fill={color} stroke="none" />}
    </svg>
  )
}

function dashFor(stroke: PipeStroke, thickness: number): string | undefined {
  if (stroke === 'solid') return undefined
  if (stroke === 'dashed') return `${thickness * 3} ${thickness * 2}`
  return `${thickness} ${thickness * 1.75}`
}

function asCardinal(direction: ElbowDirection | Cardinal | undefined): Cardinal {
  if (direction === 'up' || direction === 'down' || direction === 'left' || direction === 'right') {
    return direction
  }
  return 'right'
}

function asElbow(direction: ElbowDirection | Cardinal | undefined): ElbowDirection {
  if (
    direction === 'top-right' ||
    direction === 'top-left' ||
    direction === 'bottom-right' ||
    direction === 'bottom-left'
  ) {
    return direction
  }
  return 'bottom-right'
}

/** Named 90° lattice elbows — flush with straight runs on the grid. */
function elbowLatticePath(dir: ElbowDirection) {
  switch (dir) {
    case 'top-right':
      return 'M12 0 V12 H24'
    case 'top-left':
      return 'M12 0 V12 H0'
    case 'bottom-right':
      return 'M12 24 V12 H24'
    case 'bottom-left':
      return 'M12 24 V12 H0'
  }
}

/** Default first-leg angle for a named elbow when using `degrees`. */
function elbowRotationFor(dir: ElbowDirection): number {
  switch (dir) {
    case 'top-right':
      return 270 // up → right
    case 'top-left':
      return 180 // left → up
    case 'bottom-right':
      return 0 // right → down
    case 'bottom-left':
      return 90 // down → left
  }
}

function resolveElbowPath(opts: {
  direction?: ElbowDirection | Cardinal
  degrees?: number
  rotation?: number
  from?: number
  to?: number
}): string {
  const { direction, degrees, rotation, from, to } = opts

  if (from !== undefined && to !== undefined) {
    return angledElbowPath(from, to)
  }

  const named = asElbow(direction)
  const useAngle =
    degrees !== undefined || rotation !== undefined || from !== undefined || to !== undefined

  // Classic lattice path when no angle props — stays flush with grid runs.
  if (!useAngle) {
    return elbowLatticePath(named)
  }

  const start = from ?? rotation ?? elbowRotationFor(named)
  const sweep = degrees ?? (to !== undefined ? to - start : 90)
  const end = to ?? start + sweep
  return angledElbowPath(start, end)
}

/**
 * Two legs from the cell center. Angles in degrees: 0 = right, 90 = down
 * (clockwise, matching SVG y-down).
 */
function angledElbowPath(fromDeg: number, toDeg: number): string {
  const cx = U / 2
  const cy = U / 2
  const r = U / 2
  const rad = (deg: number) => (deg * Math.PI) / 180
  const x1 = cx + r * Math.cos(rad(fromDeg))
  const y1 = cy + r * Math.sin(rad(fromDeg))
  const x2 = cx + r * Math.cos(rad(toDeg))
  const y2 = cy + r * Math.sin(rad(toDeg))
  return `M${round(x1)} ${round(y1)} L${cx} ${cy} L${round(x2)} ${round(y2)}`
}

function round(n: number) {
  return Math.round(n * 100) / 100
}

function teePath(branch: Cardinal) {
  switch (branch) {
    case 'up':
      return 'M0 12 H24 M12 12 V0'
    case 'down':
      return 'M0 12 H24 M12 12 V24'
    case 'left':
      return 'M12 0 V24 M12 12 H0'
    case 'right':
      return 'M12 0 V24 M12 12 H24'
  }
}

function capPath(from: Cardinal) {
  switch (from) {
    case 'left':
      return 'M0 12 H12'
    case 'right':
      return 'M24 12 H12'
    case 'up':
      return 'M12 0 V12'
    case 'down':
      return 'M12 24 V12'
  }
}

function arrowPointsHorizontal(major: number, cy: number, flip: boolean) {
  const x = major / 2
  return flip
    ? `${x + 4},${cy - 4} ${x - 5},${cy} ${x + 4},${cy + 4}`
    : `${x - 4},${cy - 4} ${x + 5},${cy} ${x - 4},${cy + 4}`
}

function arrowPointsVertical(cx: number, major: number, flip: boolean) {
  const y = major / 2
  return flip
    ? `${cx - 4},${y + 4} ${cx},${y - 5} ${cx + 4},${y + 4}`
    : `${cx - 4},${y - 4} ${cx},${y + 5} ${cx + 4},${y - 4}`
}
